/**
 * Compact number formatting helpers.
 *
 * numbers at/above 1000 render with a k / m / b / t suffix (one decimal,
 * trailing ".0" trimmed) — e.g. 999 → "999", 1_500 → "1.5k", 2_000_000 → "2m".
 */

const COMPACT_UNITS: { threshold: number; divisor: number; suffix: string }[] = [
  { threshold: 1_000_000_000_000, divisor: 1_000_000_000_000, suffix: 't' },
  { threshold: 1_000_000_000, divisor: 1_000_000_000, suffix: 'b' },
  { threshold: 1_000_000, divisor: 1_000_000, suffix: 'm' },
  { threshold: 1_000, divisor: 1_000, suffix: 'k' },
]

/**
 * Scales a value into its compact form (e.g. "1.5k", "2m") or "" when the
 * value is below the smallest threshold.
 */
function compactScale(value: number): string {
  const abs = Math.abs(value)
  for (const { threshold, divisor, suffix } of COMPACT_UNITS) {
    if (abs >= threshold) {
      const formatted = (value / divisor).toFixed(1).replace(/\.0$/, '')
      return `${formatted}${suffix}`
    }
  }
  return ''
}

/**
 * Formats a number so it displays as 1k / 1m / 1b / 1t once it reaches 1,000.
 * Values below 1,000 are rounded to whole numbers.
 *
 * @example formatCompactNumber(999) === '999'
 * @example formatCompactNumber(1_500) === '1.5k'
 * @example formatCompactNumber(2_000_000) === '2m'
 */
export const formatCompactNumber = (value: number): string => {
  if (!Number.isFinite(value)) return String(value)
  const compact = compactScale(value)
  return compact || String(Math.round(value))
}

/**
 * Formats a money amount with the same compact behaviour, keeping two decimals
 * below 1,000. Pass a currency code to append it to the result.
 *
 * @example formatCompactMoney(999.5) === '999.50'
 * @example formatCompactMoney(1_234.5) === '1.2k'
 * @example formatCompactMoney(2_000_000, 'USD') === '2m USD'
 */
export const formatCompactMoney = (value: number, currencyCode?: string): string => {
  if (!Number.isFinite(value)) return String(value)
  const compact = compactScale(value)
  const formatted = compact || value.toFixed(2)
  return currencyCode ? `${formatted} ${currencyCode}` : formatted
}
